import { useEmailSubscriptionStore } from '@/stores/emailSubscriptionStore'
import { EmailSubscriptionFactory } from '@/factories/emailSubscriptionFactory'
import type { EmailSubscription } from '@/types/email'
import type { WhereFilterOp } from 'firebase/firestore'

/**
 * Email Subscription Service - Provides business logic for email subscription operations
 */
export function useEmailSubscriptionService() {
  const emailSubscriptionStore = useEmailSubscriptionStore()

  return {
    /**
     * Get active subscriptions for a person
     */
    async getPersonSubscriptions(personId: string): Promise<EmailSubscription[]> {
      const conditions = [
        { field: 'personId', op: '==' as WhereFilterOp, value: personId },
        { field: 'status', op: '==' as WhereFilterOp, value: 'active' },
      ]
      return await emailSubscriptionStore.query(conditions)
    },

    /**
     * Get active subscriptions for an organization
     */
    async getOrganizationSubscriptions(organizationId: string): Promise<EmailSubscription[]> {
      const conditions = [
        { field: 'organizationId', op: '==' as WhereFilterOp, value: organizationId },
        { field: 'status', op: '==' as WhereFilterOp, value: 'active' },
      ]
      return await emailSubscriptionStore.query(conditions)
    },

    /**
     * Subscribe a person or organization to an email list
     * Checks for existing subscriptions before creating a new one
     */
    async subscribeToList(data: {
      listId: string
      email: string
      personId?: string
      organizationId?: string
    }): Promise<EmailSubscription> {
      // Check for existing active subscription
      const conditions = [
        { field: 'listId', op: '==' as WhereFilterOp, value: data.listId },
        { field: 'email', op: '==' as WhereFilterOp, value: data.email },
        { field: 'status', op: '==' as WhereFilterOp, value: 'active' },
      ]
      const existingSubs = await emailSubscriptionStore.query(conditions)

      if (existingSubs.length > 0) {
        // If there's an existing active subscription, return it
        return existingSubs[0]
      }

      // Check for unsubscribed subscription that we can reactivate
      const unsubscribedConditions = [
        { field: 'listId', op: '==' as WhereFilterOp, value: data.listId },
        { field: 'email', op: '==' as WhereFilterOp, value: data.email },
        { field: 'status', op: '==' as WhereFilterOp, value: 'unsubscribed' },
      ]
      const unsubscribedSubs = await emailSubscriptionStore.query(unsubscribedConditions)

      if (unsubscribedSubs.length > 0 && unsubscribedSubs[0].id) {
        // Reactivate the unsubscribed subscription
        await emailSubscriptionStore.update(unsubscribedSubs[0].id, {
          status: 'active',
          subscribedAt: new Date(),
          unsubscribedAt: undefined,
        })
        return {
          ...unsubscribedSubs[0],
          status: 'active',
          subscribedAt: new Date(),
          unsubscribedAt: undefined,
        }
      }

      // Create new subscription if none exists
      const subscription = EmailSubscriptionFactory.create({
        ...data,
        status: 'active',
        subscribedAt: new Date(),
      })

      const addedSubscription = await emailSubscriptionStore.add(subscription)
      if (!addedSubscription.id) {
        throw new Error('Failed to get subscription ID')
      }

      return addedSubscription
    },

    /**
     * Unsubscribe a person or organization from an email list
     */
    async unsubscribeFromList(
      listId: string,
      personId?: string,
      organizationId?: string,
    ): Promise<void> {
      const conditions = [
        { field: 'listId', op: '==' as WhereFilterOp, value: listId },
        {
          field: personId ? 'personId' : 'organizationId',
          op: '==' as WhereFilterOp,
          value: personId || organizationId,
        },
      ]
      const subs = await emailSubscriptionStore.query(conditions)

      if (subs.length > 0 && subs[0].id) {
        await emailSubscriptionStore.update(subs[0].id, {
          status: 'unsubscribed',
          unsubscribedAt: new Date(),
        })
      }
    },

    /**
     * Check if a person or organization is subscribed to a list
     */
    async isSubscribed(
      listId: string,
      personId?: string,
      organizationId?: string,
    ): Promise<boolean> {
      const conditions = [
        { field: 'listId', op: '==' as WhereFilterOp, value: listId },
        {
          field: personId ? 'personId' : 'organizationId',
          op: '==' as WhereFilterOp,
          value: personId || organizationId,
        },
        { field: 'status', op: '==' as WhereFilterOp, value: 'active' },
      ]
      const subs = await emailSubscriptionStore.query(conditions)
      return subs.length > 0
    },
  }
}
